import type { LoginRequest, LoginResponse, TokenValidationRequest } from 'backend/types'
import { API_BASE_URL, BASE_PATH, isLocalDev } from '@/lib/env'
import { useAuthStore } from '@/stores/auth'
import { apiFetch } from '..'

/**
 * ログインAPI
 *
 * @param email - ログインID（メールアドレス）
 * @param password - パスワード（平文、バックエンドでMD5ハッシュ化）
 * @param persisted - ログイン状態を維持するか（true: 14日間, false: セッション）
 * @returns ログインレスポンス（トークン、ユーザー情報、ACL等）
 * @throws ApiError - ログイン失敗時（403: 認証失敗, 500: サーバーエラー）
 */
export async function login(email: string, password: string, persisted: boolean): Promise<LoginResponse> {
  const requestBody: LoginRequest = {
    email,
    password,
    persisted,
  }

  return apiFetch<LoginResponse>('/api/login', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  })
}

/**
 * トークン検証API
 *
 * OIDCコールバック後のトークン検証や、既存トークンの再検証に使用します。
 * 最新のACL・ロール情報を取得し、セッションを更新します。
 *
 * @param token - セッショントークン
 * @param state - OAuth2ステートパラメータ（オプション）
 * @returns トークン検証レスポンス（ユーザー情報、ACL等）
 * @throws ApiError - トークン無効時（404: トークン未発見, 400: アカウント未発見）
 */
export async function validateToken(token: string, state?: string): Promise<LoginResponse> {
  const requestBody: TokenValidationRequest = {
    token,
    ...(state && { state }),
  }

  return apiFetch<LoginResponse>('/api/login/token', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  })
}

/**
 * ログアウトAPI
 *
 * バックエンドの `/api/logout` を呼び出してRedisセッションを削除し、
 * 認証ストアをクリアしてログイン画面にリダイレクトする。
 * API呼び出しが失敗してもログアウト処理は中断せず続行する。
 *
 * - local環境 (localhost): `${BASE_PATH}/login`（例: /opportunity-portal/login）
 * - dev/本番環境: `/login` (nginxがdracaenaのログイン画面にルーティング)
 */
export async function logout(): Promise<void> {
  const state = useAuthStore.getState()

  try {
    if (state.token) {
      // apiFetchは401時にlogout()を呼ぶため、ここで使うと無限ループになる。直接fetchを使用。
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: state.token }),
      })

      if (!response.ok) {
        console.warn(`Logout API returned ${response.status}`)
      }
    }
  } catch (error) {
    console.error('Logout API call failed:', error)
  }

  state.clearUser()

  if (typeof window !== 'undefined') {
    const loginUrl = isLocalDev ? `${BASE_PATH}/login` : '/login'
    window.location.replace(loginUrl)
  }
}
